import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { useProjects } from '../contexts/ProjectContext';
import { useContextEngine } from '../contexts/ContextEngineContext';
import { SignalCategory } from '../types/context.types';
import ProjectBrainModal from '../components/context/ProjectBrainModal';
import './Onboarding.css';

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const { createProject, selectProject } = useProjects();
  const { processOnboarding, markOnboardingComplete, processingContext, hasCompletedOnboarding } = useContextEngine();
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null);

  // If onboarding already completed, redirect to home
  useEffect(() => {
    if (hasCompletedOnboarding) {
      navigate('/home', { replace: true });
    }
  }, [hasCompletedOnboarding, navigate]);

  const ensureProject = async (projectName: string): Promise<string> => {
    if (createdProjectId) return createdProjectId;

    const newProject = await createProject({ name: projectName });
    selectProject(newProject);
    setCreatedProjectId(newProject.id);
    return newProject.id;
  };

  const handleComplete = async (result: {
    projectName: string;
    files: Array<{ fileName: string; fileType: string; fileSize: number; data: string }>;
    manualTags: Array<{ label: string; category: SignalCategory }>;
  }) => {
    const projectId = await ensureProject(result.projectName);

    const onboardingResult = await processOnboarding(
      projectId,
      result.files,
      result.manualTags
    );

    return {
      sources: onboardingResult.sources,
      signals: onboardingResult.signals,
      readiness: onboardingResult.readiness,
    };
  };

  const handleContinue = async (projectName: string) => {
    try {
      const projectId = await ensureProject(projectName);
      await markOnboardingComplete();
      navigate('/home', { state: { preselectedProjectId: projectId } });
    } catch (err) {
      console.error('Error completing onboarding:', err);
      navigate('/home');
    }
  };

  return (
    <div className="onboarding-page">
      <Container className="onboarding-container">
        <ProjectBrainModal
          onComplete={handleComplete}
          onContinue={handleContinue}
          processing={processingContext}
          showProjectName
        />
      </Container>
    </div>
  );
};

export default Onboarding;
